'use client';

import React from 'react';

import { Rearrange } from '@depop/web-thrift-ui/Icons/Rearrange';
import { Body } from '@depop/web-thrift-ui/Typography';
import { RearrangeIcon } from '@depop/web-ui-kit/Icons/RearrangeIcon';
import { useQuery } from '@tanstack/react-query';
import { clsx } from 'clsx';
import { isAfter, isBefore } from 'date-fns';
import { useParams } from 'next/navigation';
import { useTranslations } from 'next-intl';

import { ShopDrop } from '../ShopDrop';
import { ShopProductsEmptyStateVariant } from '../ShopProductsEmptyStateVariant';

import styles from './styles.module.css';

import { ShopFilters } from '@/app/[locale]/[username]/(shop)/components/Filters';
import { ShopProductsList } from '@/app/[locale]/[username]/(shop)/components/ProductList';
import { ShopQRCodeModalButton } from '@/app/[locale]/[username]/(shop)/components/ShopQRCodeModalButton';
import { SoldProducts } from '@/app/[locale]/[username]/(shop)/components/SoldProducts';
import { ButtonExperiment } from '@/components/ButtonExperiment';
import { shopDropQueryOptions } from '@/modules/cms/queries';
import { VARIANT_IDENTIFIER } from '@/modules/experiments/config.ts';
import { useExperiments } from '@/modules/experiments/useExperiments.ts';
import { getObjectsFromPages } from '@/modules/pagination/getObjectsFromPages';
import { useFilteredProductsQueryKey } from '@/modules/shop/hooks/useFilteredProductsQueryKey';
import { useFilteredShopProducts } from '@/modules/shop/hooks/useFilteredShopProducts';
import { useIsViewingOwnShop } from '@/modules/shop/hooks/useIsViewingOwnShop';
import { useRearrangeShop } from '@/modules/shop/hooks/useRearrangeShop';
import { useShopByUsername } from '@/modules/shop/hooks/useShopByUsername';
import { useShopDropSignupComplete } from '@/modules/shop/hooks/useShopDropSignupComplete';
import { useShopFilters } from '@/modules/shop/hooks/useShopFilters';
import { useSoldShopProducts } from '@/modules/shop/hooks/useSoldShopProducts';

export function ShopProducts() {
  const tShop = useTranslations('shop');
  const { data: sellerData } = useShopByUsername();
  const isViewingOwnShop = useIsViewingOwnShop();
  const { username } = useParams<{ username: string; locale: string }>();
  const sellerId = sellerData?.data.id;

  useShopDropSignupComplete(sellerId);

  const { showDesktopFilters, showMobileFilters } = useShopFilters(sellerId);

  const {
    data: activeData,
    isError: isActiveError,
    isFetchingNextPage: isFetchingActiveNextPage,
    isLoading: isActiveLoading,
    isRefetching,
    fetchNextPage: fetchActiveNextPage,
    hasNextPage: hasActiveNextPage,
  } = useFilteredShopProducts(sellerId);

  const {
    data: soldData,
    isError: isSoldError,
    isFetchingNextPage: isFetchingSoldNextPage,
    isLoading: isSoldLoading,
    fetchNextPage: fetchSoldNextPage,
    hasNextPage: hasSoldNextPage,
  } = useSoldShopProducts(sellerId);

  const activeProducts = getObjectsFromPages(activeData);
  const soldProducts = getObjectsFromPages(soldData);

  const isSoldOut = !activeProducts?.length && !!soldProducts?.length;

  const allActivePagesLoaded =
    !hasActiveNextPage && !isActiveLoading && !!activeProducts?.length;

  const { web_qr_code_shop_2025_q3, web_thrift_ui_button } = useExperiments([
    'web_qr_code_shop_2025_q3',
    'web_thrift_ui_button',
  ]);
  const isThriftButtonVariant = web_thrift_ui_button === VARIANT_IDENTIFIER;

  const { data: shopDropConfig } = useQuery(shopDropQueryOptions(username));

  const now = new Date();
  const shouldDisplayShopDrop =
    shopDropConfig &&
    isAfter(now, shopDropConfig.dropCountdownDate) &&
    isBefore(now, shopDropConfig.showUntil);

  const queryKey = useFilteredProductsQueryKey(sellerId);

  const {
    mutation,
    canRearrange,
    isRearranging,
    start,
    stop,
    swap,
    save,
    rearrangedProducts,
  } = useRearrangeShop({ products: activeProducts, queryKey });

  function handleActiveEndReached() {
    if (hasActiveNextPage) {
      fetchActiveNextPage();
    }
  }

  return (
    <div>
      {shouldDisplayShopDrop && (
        <ShopDrop
          className={styles.shopDropWrapper}
          dropDate={shopDropConfig.dropDate}
          showUntil={shopDropConfig.showUntil}
          firstName={sellerData?.data.first_name || username}
          timeZoneName={shopDropConfig.timeZoneName}
        />
      )}
      <div>
        <div className={styles.activeHeading}>
          {isViewingOwnShop && !!activeProducts?.length && (
            <span className={styles.activeHeadingText}>
              <Body variant="sm-bold">{tShop('Active')}</Body>
              <Body variant="sm-regular">
                (
                {tShop('ActiveProductsListing', {
                  products: activeProducts.length,
                })}
                )
              </Body>
            </span>
          )}

          {canRearrange && (
            <div
              className={clsx(styles.rearrangeButtonsContainer, {
                [styles.rearrangeButtonsContainerThrift]: isThriftButtonVariant,
              })}
            >
              {isRearranging ? (
                <div className={styles.rearrangeButtons}>
                  <ButtonExperiment
                    variant="tertiary"
                    legacyOutline={false}
                    legacyClassName={clsx(
                      styles.actionButton,
                      styles.rearrangeModeActiveButton
                    )}
                    onClick={stop}
                    disabled={mutation.isPending || mutation.isSuccess}
                  >
                    {tShop('Cancel')}
                  </ButtonExperiment>
                  <ButtonExperiment
                    variant="tertiary"
                    legacyOutline={false}
                    legacyClassName={clsx(
                      styles.actionButton,
                      styles.rearrangeModeActiveButton
                    )}
                    onClick={save}
                    isLoading={mutation.isPending}
                    legacyIsSuccess={mutation.isSuccess}
                    disabled={mutation.isPending || mutation.isSuccess}
                  >
                    {tShop('Save')}
                  </ButtonExperiment>
                </div>
              ) : (
                <div className={styles.rearrangeButtons}>
                  <ButtonExperiment
                    variant="tertiary"
                    legacyOutline={false}
                    icon={<Rearrange size="md" />}
                    legacyIcon={<RearrangeIcon size={18} />}
                    onClick={start}
                    disabled={!canRearrange}
                    legacyClassName={clsx(
                      styles.actionButton,
                      styles.rearrangeModeActiveButton
                    )}
                  >
                    {tShop('Rearrange')}
                  </ButtonExperiment>
                  {web_qr_code_shop_2025_q3 === VARIANT_IDENTIFIER && (
                    <ShopQRCodeModalButton />
                  )}
                </div>
              )}
            </div>
          )}
        </div>
        {(showDesktopFilters || showMobileFilters) && <ShopFilters />}
        <ShopProductsList
          className={styles.productListContainer}
          hasMore={hasActiveNextPage}
          isFetchingNextPage={isFetchingActiveNextPage}
          products={isRearranging ? rearrangedProducts : activeProducts}
          onEndReached={handleActiveEndReached}
          isLoading={isActiveLoading || isRefetching || isSoldLoading}
          isError={isActiveError}
          EmptyState={() => (
            <ShopProductsEmptyStateVariant
              isSoldOut={isSoldOut}
              soldProductIds={soldProducts.map((product) => product.id)}
            />
          )}
          isRearranging={isRearranging}
          handleDragEnd={swap}
        />
      </div>

      {!!soldProducts?.length && (isSoldOut || allActivePagesLoaded) && (
        <SoldProducts
          hasMore={hasSoldNextPage}
          isFetchingNextPage={isFetchingSoldNextPage}
          products={soldProducts}
          onEndReached={fetchSoldNextPage}
          isLoading={isSoldLoading}
          isError={isSoldError}
        />
      )}
    </div>
  );
}
